/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {PropulsionPortfolioLoadResult} from '../models/propulsion-portfolio-load-result.model';
import {PropulsionPortfolioLoadViolation} from '../models/propulsion-portfolio-load-violation.model';
import {PropulsionPortfolioLoad} from '../models/propulsion-portfolio-load.model';
import {PropulsionPortfolioReportRequest} from '../models/reports/propulsion-portfolio-report-request.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})
export class PropulsionPortfolioLoadService {

  // Constant to hold the endpoint value
  private propulsionPortfolioLoadUrl: string;

  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
    this.propulsionPortfolioLoadUrl = `${this.configService.baseApiUrl}portfolio/propulsion/transform`;
  }

  /**
   * Validate the propulsion portfolio load template
   * @param loadTemplate The load template containing the propulsion portfolio load data
   * @return Observable<Map<string, PropulsionPortfolioLoadViolation>> - returns mapping of lists of violations
   */
  validate(loadTemplate: Map<string, PropulsionPortfolioLoad[]>):
    Observable<Map<string, PropulsionPortfolioLoadViolation>> {
    const url = `${this.configService.baseApiUrl}portfolio/propulsion/validate`;
    return this.http.post<Map<string, PropulsionPortfolioLoadViolation>>(
      url, loadTemplate).pipe(catchError(error => this.handleError(error))
    );
  }

  /**
   * Get the current portfolio by port type
   * @param portType portfolio type
   */
  getCurrentPortfolioByPortType(portType: string): Observable<any> {
    const params = new HttpParams().set('portType', portType);
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/propulsion/current-portfolio`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the portfolio report
   */
  getPortfolioReport(request: PropulsionPortfolioReportRequest) {
    return this.http.post(`${this.configService.baseApiUrl}portfolio/propulsion/report`, request, {
      responseType: 'blob' as 'json'
    })
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Gets the portfolio report filters
   * @param portType the portfolio type
   * @param fcorgs the list of fcorgs
   */
  getPortfolioReportFilters(portType: string, fcorgs: string[]) {
    const fcorgString = fcorgs.join(',');
    const params = new HttpParams().set("port-type", portType)
    .set("fcorgs", fcorgString);
    return this.http.get(`${this.configService.baseApiUrl}portfolio/propulsion/filters`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }


  /**
   * Load the propulsion portfolio template
   * @param loadTemplate The load template containing the propulsion portfolio load data
   * @param isInitialLoad boolean to indicate if the load is an initial load
   * @param gmid the GMID of the user
   * @return Observable<Map<string, PropulsionPortfolioLoadResult>> - returns mapping of result lists of the load
   */
  load(loadTemplate: Map<string, PropulsionPortfolioLoad[]>,
       isInitialLoad: boolean,
       gmid: string): Observable<Map<string, PropulsionPortfolioLoadResult>> {
    const parameters = `?isInitialLoad=${isInitialLoad}&gmid=${gmid}`;
    const url = `${this.configService.baseApiUrl}portfolio/propulsion` + parameters;
    return this.http.post<Map<string, PropulsionPortfolioLoadResult>>(
      url, loadTemplate).pipe(catchError(error => this.handleError(error))
    );
  }

  /**
   * Transforms the Portfolio Load template the user uploads to an object to represent the data
   * @param file the portfolio load template file that is uploaded
   * @param sheetList a list containing the selected sheets to read data from
   */
  transform(file: File, sheetList: string[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    return this.http.post<any>(this.propulsionPortfolioLoadUrl, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Deletes from Propulsion Portfolio based off list of IDs
   * @param idList - id list of Propulsion Portfolio records to delete
   */
  delete(idList: number[]): Observable<any> {
    return this.http.delete(`${this.configService.baseApiUrl}portfolio/propulsion`, {
      body: idList
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Error handler
   * @param errorResponse the Http Error response
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unexpected error occurred';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
